import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "./useAppContext";

export function useTrackActivity() {
  const location = useLocation();
  const { authUser } = useAppContext();

  useEffect(() => {
    if (!authUser?.id) return;

    // Update last activity when page changes
    const updateActivity = async () => {
      try {
        // Extract just the pathname (slug) - remove leading slash if present
        const pageSlug = location.pathname.startsWith("/")
          ? location.pathname.substring(1)
          : location.pathname;

        await supabase
          .from("users")
          .update({
            last_active_on: new Date().toISOString(),
            last_page_viewed: pageSlug,
          })
          .eq("auth_id", authUser.id);
      } catch (error) {
        console.error("Failed to update activity:", error);
      }
    };

    updateActivity();
  }, [location.pathname, authUser?.id]);

  // Also track when user interacts (clicks, scrolls)
  useEffect(() => {
    if (!authUser?.id) return;

    let activityTimeout: NodeJS.Timeout;

    const trackActivity = () => {
      // Debounce activity updates to once per minute
      clearTimeout(activityTimeout);

      activityTimeout = setTimeout(async () => {
        try {
          await supabase
            .from("users")
            .update({
              last_active_on: new Date().toISOString(),
            })
            .eq("auth_id", authUser.id);
        } catch (error) {
          console.error("Failed to update activity:", error);
        }
      }, 60000); // 1 minute
    };

    // Track various user interactions
    window.addEventListener("click", trackActivity);
    window.addEventListener("scroll", trackActivity);
    window.addEventListener("keypress", trackActivity);

    return () => {
      clearTimeout(activityTimeout);
      window.removeEventListener("click", trackActivity);
      window.removeEventListener("scroll", trackActivity);
      window.removeEventListener("keypress", trackActivity);
    };
  }, [authUser?.id]);
}
